// src/pages/FileManager/FileManagerPage.tsx
import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FileManagerProvider, useFileManager } from '../../contexts/FileManagerContext';
import FileSidebar from '../../components/files/FileSidebar';
import FileBreadcrumb from '../../components/files/FileBreadcrumb';
import FileToolbar from '../../components/files/FileToolbar';
import FileGrid from '../../components/files/FileGrid';
import FileList from '../../components/files/FileList';
import FileUploadZone from '../../components/files/FileUploadZone';
import TrashView from '../../components/files/TrashView';
import SharedView from '../../components/files/SharedView';
import FavoritesView from '../../components/files/FavoritesView';
import RecentsView from '../../components/files/RecentsView';
import SearchResults from '../../components/files/SearchResults';
import EmptyState from '../../components/files/EmptyState';
import FolderDialog from '../../components/files/FolderDialog';

const FileManagerContent: React.FC = () => {
  const { t } = useTranslation();
  const { state, navigateToSection, uploadDocument } = useFileManager();
  const { section, contents, loading, error, viewMode } = state;
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialize with my-space section
  useEffect(() => {
    navigateToSection('my-space');
  }, [navigateToSection]);

  const handleCreateFolder = () => {
    setFolderDialogOpen(true);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        try {
          await uploadDocument(files[i]);
        } catch (error) {
          console.error('Upload failed:', error);
        }
      }
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            minHeight: 400,
          }}
        >
          <CircularProgress size={48} sx={{ color: 'primary.main' }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('fileManager.loading', 'Chargement...')}
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      );
    }

    // Search view
    if (section === 'search') {
      return <SearchResults />;
    }

    // Trash view
    if (section === 'trash') {
      return <TrashView />;
    }

    // Shared view
    if (section === 'shared') {
      return <SharedView />;
    }

    // My space / folders view
    if (section === 'my-space') {
      const isEmpty =
        !contents || (contents.folders.length === 0 && contents.documents.length === 0);

      if (isEmpty) {
        return (
          <EmptyState
            onCreateFolder={handleCreateFolder}
            onUpload={handleUploadClick}
          />
        );
      }

      return viewMode === 'grid' ? <FileGrid /> : <FileList />;
    }

    // Favorites view
    if (section === 'favorites') {
      return <FavoritesView />;
    }

    // Recent view
    if (section === 'recent') {
      return <RecentsView />;
    }

    return null;
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          height: 'calc(100vh - 120px)',
          bgcolor: 'background.default',
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        {/* Sidebar */}
        <FileSidebar />

        {/* Main content */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          {/* Toolbar */}
          <FileToolbar />

          {/* Breadcrumb */}
          <Box sx={{ px: 3, borderBottom: '1px solid', borderColor: 'grey.100' }}>
            <FileBreadcrumb />
          </Box>

          {/* Upload zone + content */}
          <FileUploadZone>
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                p: 3,
              }}
            >
              {renderContent()}
            </Box>
          </FileUploadZone>
        </Box>
      </Box>

      {/* Hidden file input for empty state upload */}
      <input
        type="file"
        ref={fileInputRef}
        hidden
        multiple
        onChange={handleFileSelect}
      />

      {/* Folder dialog for empty state */}
      <FolderDialog
        open={folderDialogOpen}
        onClose={() => setFolderDialogOpen(false)}
        mode="create"
      />
    </>
  );
};

const FileManagerPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
      {/* Page header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'grey.800' }}>
          {t('fileManager.title', 'Gestionnaire de fichiers')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('fileManager.subtitle', 'Organisez et gerez vos fichiers et dossiers')}
        </Typography>
      </Box>

      {/* File Manager */}
      <FileManagerProvider>
        <FileManagerContent />
      </FileManagerProvider>
    </Box>
  );
};

export default FileManagerPage;
